/**
 * V2 place metadata — locations.metadata (JSONB) holds Google Place
 * details; when stale (>7 days) or missing business_status, refresh via
 * the same-origin proxy with extended=true and PERSIST the result back
 * through the set_location_metadata RPC (supabase/migrations/
 * 20260714_place_metadata_writeback.sql). V1 refreshed per view without writing
 * back, which paid the top Place Details SKU (Enterprise + Atmosphere)
 * on every view of a stale place — the write-back makes it one Google
 * call per place per window. Co-visited ("Also popular with visitors")
 * ports V1's client-side visitor-overlap ranking.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { getApiBase } from '../../core/config.js';

const PROXY = `${getApiBase()}/google-proxy.php`;

export const PRICE_MAP = {
  PRICE_LEVEL_FREE: 'Free',
  PRICE_LEVEL_INEXPENSIVE: '$',
  PRICE_LEVEL_MODERATE: '$$',
  PRICE_LEVEL_EXPENSIVE: '$$$',
  PRICE_LEVEL_VERY_EXPENSIVE: '$$$$',
  0: 'Free', 1: '$', 2: '$$', 3: '$$$', 4: '$$$$',
};

// V1 used 3 days (location-panel.js:135) back when nothing persisted, so the
// window only gated the proxy cache. Now that refreshes write back, the
// window IS the per-place Google spend; 7 days still catches closures fast
// enough (admin can also stamp closures by hand) at under half the calls.
// Google's caching policy allows up to 30 days.
const REFRESH_DAYS = 7;

/**
 * Stale after REFRESH_DAYS, or predates business-status tracking or the
 * photo restore (each a one-time backfill: after the refresh the key is
 * present — null when Google has nothing — and the 7-day window rules).
 */
export function needsMetadataRefresh(metadata) {
  if (!metadata || !metadata.fetched_at) return true;
  if (!('business_status' in metadata)) return true;
  if (!('photo_url' in metadata)) return true;
  const days = (Date.now() - new Date(metadata.fetched_at).getTime()) / 86400000;
  return days > REFRESH_DAYS;
}

/** V1's extended place-details mapping (location-panel.js:86-120). */
export async function fetchRichPlaceDetails(placeId) {
  const response = await fetch(
    `${PROXY}?action=place-details&placeId=${encodeURIComponent(placeId)}&extended=true`
  );
  const data = await response.json();
  if (data.error) return null;
  return {
    formatted_address: data.formattedAddress || null,
    place_id: placeId,
    phone: data.nationalPhoneNumber || null,
    website: data.websiteUri || null,
    google_maps_url: data.googleMapsUri || null,
    business_status: data.businessStatus || null,
    rating: data.rating || null,
    rating_count: data.userRatingCount || null,
    price_level: data.priceLevel || null,
    type: data.primaryType || null,
    hours: data.regularOpeningHours?.weekdayDescriptions || null,
    description: data.editorialSummary?.text || null,
    // Google's photo, as a URL into our own proxy's `photo` action (key
    // stays server-side, bytes disk-cached 7 days) — V1's exact pattern
    // (location-panel.js:100-105), dropped in the V2 port until
    // 2026-07-27. The attribution rides along because Places policy
    // requires showing it wherever the photo shows.
    photo_url: data.photos?.[0]?.name
      ? `${PROXY}?action=photo&photoName=${encodeURIComponent(data.photos[0].name)}&maxHeight=800&maxWidth=1200`
      : null,
    photo_attribution: data.photos?.[0]?.authorAttributions?.[0]?.displayName || null,
    fetched_at: new Date().toISOString(),
  };
}

const STATUS_MAP = {
  OPERATIONAL: 'open',
  CLOSED_PERMANENTLY: 'closed_permanently',
  CLOSED_TEMPORARILY: 'closed_temporarily',
};

/**
 * Persist what the refresh saw into locations.status (M8 SQL batch —
 * the column write M6 deferred). Fire-and-forget through the narrow
 * set_location_status RPC; anon viewers lack execute and that's fine.
 */
function persistStatus(location, businessStatus) {
  const status = STATUS_MAP[businessStatus];
  if (!status || status === (location.status || 'open')) return;
  // An admin pin outranks Google (supabase/migrations/20260810_admin_status_override.sql).
  // The RPC refuses these anyway; skipping here saves the pointless round
  // trip AND keeps the client from believing it changed something.
  if (location.metadata?.status_source === 'admin') return;
  gateway.rawRpc('set_location_status', {
    params: { p_location_id: location.id, p_status: status },
  }).catch(() => {});
}

/**
 * Persist the refreshed details onto the row so the NEXT view (any
 * viewer) reads them from the database instead of paying Google again.
 * Fire-and-forget like persistStatus: anon viewers lack execute on the
 * RPC (and pre-SQL-apply it 404s) — both fine, the render already has
 * the fresh data in hand.
 */
function persistMetadata(location, fresh) {
  gateway.rawRpc('set_location_metadata', {
    params: { p_location_id: location.id, p_metadata: fresh },
  }).catch(() => {});
}

/** The location's metadata, refreshed + persisted when stale. Never throws. */
export async function getMetadata(location) {
  let metadata = location.metadata || {};
  if (location.google_place_id && needsMetadataRefresh(metadata)) {
    try {
      const fresh = await fetchRichPlaceDetails(location.google_place_id);
      if (fresh) {
        metadata = { ...metadata, ...fresh };
        if (location.id) persistMetadata(location, fresh);
        persistStatus(location, fresh.business_status);
      }
    } catch {
      // proxy unreachable — render what the row already has
    }
  }
  return metadata;
}

/**
 * The place page's "this place is gone" report (2026-08-09). Two moves:
 * an on-the-spot Google re-check with the CHEAPEST field mask (only
 * businessStatus — nowhere near the Enterprise+Atmosphere SKU the full
 * refresh pays), auto-stamping via set_location_status when Google
 * agrees; and, when Google still says OPERATIONAL — it lags reality
 * after a disaster, exactly when the person standing in front of the
 * rubble knows first — a flag for admin review through the
 * report_location_closed RPC (supabase/migrations/20260809_report_location_closed.sql;
 * pre-apply it 404s and this reports 'failed'). Returns
 * 'confirmed_permanent' | 'confirmed_temporary' | 'reported' | 'failed'
 * so the page can say what happened in plain words. Never throws.
 */
export async function reportPlaceGone(location) {
  let businessStatus = null;
  if (location.google_place_id) {
    try {
      const response = await fetch(
        `${PROXY}?action=place-details&placeId=${encodeURIComponent(location.google_place_id)}&fields=businessStatus`
      );
      const data = await response.json();
      if (!data.error) businessStatus = data.businessStatus || null;
    } catch { /* proxy unreachable — the report below still counts */ }
  }
  const status = STATUS_MAP[businessStatus];
  if (status === 'closed_permanently' || status === 'closed_temporarily') {
    try {
      const { error } = await gateway.rawRpc('set_location_status', {
        params: { p_location_id: location.id, p_status: status },
      });
      if (!error) return status === 'closed_permanently' ? 'confirmed_permanent' : 'confirmed_temporary';
    } catch { /* fall through to the review flag */ }
  }
  try {
    const { error } = await gateway.rawRpc('report_location_closed', {
      params: { p_location_id: location.id },
    });
    return error ? 'failed' : 'reported';
  } catch {
    return 'failed';
  }
}

/**
 * V1 loadAlsoVisited (location-panel.js:208-267): other places this
 * place's visitors also yoinked, ranked by distinct visitors. Exported
 * ranking for tests.
 */
export function rankCoVisited(locationId, visitorIds, otherYoinks) {
  const counts = new Map();
  for (const y of otherYoinks) {
    if (y.location_id === locationId || !y.locations?.display_name) continue;
    const entry = counts.get(y.location_id) || {
      id: y.location_id,
      name: y.locations.display_name,
      visitors: new Set(),
    };
    entry.visitors.add(y.user_id);
    counts.set(y.location_id, entry);
  }
  const minVisitors = visitorIds.length >= 3 ? 2 : 1;
  return [...counts.values()]
    .map((loc) => ({ id: loc.id, name: loc.name, count: loc.visitors.size }))
    .filter((loc) => loc.count >= minVisitors)
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
}

/**
 * Co-visited via the get_also_visited RPC
 * (supabase/migrations/20260712e_assessment_stats_rpcs.sql): the visitor-overlap join runs
 * server-side, replacing the old client fan-out — an `in.(…)` list of
 * every visitor plus a 2000-row read, the exact shape V2-PLAN §9.3
 * forbids (assessment 1.6). rankCoVisited above stays as the executable
 * spec the RPC mirrors. The logs argument only gates the call: no
 * visitors on this page means nothing to rank.
 */
export async function loadAlsoVisited(locationId, logs) {
  const visitorIds = [...new Set(logs.map((y) => y.user_id).filter(Boolean))];
  if (!visitorIds.length) return [];
  return cached(`co-visited:${locationId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_also_visited', {
      params: { p_location_id: locationId, p_limit: 6 },
    });
    if (error) throw error;
    return (data || []).map((r) => ({ id: r.id, name: r.name, count: Number(r.visitor_count) || 0 }));
  });
}
